import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import './index.scss';

function mulberry32(seed) {
  return function next() {
    let t = seed += 0x6D2B79F5;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export default function TrackWaveform({
  bars = 56,
  seed = 1,
  progress = 0,
  size = 'md',
  ariaLabel = 'Track timeline',
  onSeek,
  className = '',
}) {
  const heights = useMemo(() => {
    const rand = mulberry32(seed * 1337 + bars);
    return Array.from({ length: bars }, () => 25 + rand() * 75);
  }, [bars, seed]);

  const playedFraction = Math.max(0, Math.min(1, Number(progress) || 0));
  const isInteractive = typeof onSeek === 'function';
  const classes = [
    'track-wave',
    `track-wave--${size}`,
    isInteractive ? 'track-wave--interactive' : '',
    className,
  ].filter(Boolean).join(' ');

  const seekFromEvent = useCallback((event) => {
    if (!isInteractive) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width) {
      return;
    }
    const clientX = event.clientX ?? event.touches?.[0]?.clientX;
    if (!Number.isFinite(clientX)) {
      return;
    }
    const nextProgress = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    onSeek(nextProgress);
  }, [isInteractive, onSeek]);

  const handleKeyDown = useCallback((event) => {
    if (!isInteractive) {
      return;
    }
    const step = event.shiftKey ? 0.1 : 0.05;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      onSeek(Math.max(0, playedFraction - step));
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      onSeek(Math.min(1, playedFraction + step));
    } else if (event.key === 'Home') {
      event.preventDefault();
      onSeek(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      onSeek(1);
    }
  }, [isInteractive, onSeek, playedFraction]);

  return (
    <div
      className={classes}
      aria-hidden={isInteractive ? undefined : 'true'}
      aria-label={isInteractive ? ariaLabel : undefined}
      aria-valuemin={isInteractive ? 0 : undefined}
      aria-valuemax={isInteractive ? 100 : undefined}
      aria-valuenow={isInteractive ? Math.round(playedFraction * 100) : undefined}
      onClick={seekFromEvent}
      onKeyDown={handleKeyDown}
      role={isInteractive ? 'slider' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
    >
      {heights.map((h, i) => (
        <div
          key={i}
          className="track-wave__bar"
          style={{
            height: `${h.toFixed(0)}%`,
            '--track-wave-fill': `${Math.max(0, Math.min(1, (playedFraction * bars) - i)) * 100}%`,
          }}
        />
      ))}
    </div>
  );
}

TrackWaveform.propTypes = {
  bars: PropTypes.number,
  seed: PropTypes.number,
  progress: PropTypes.number,
  size: PropTypes.oneOf(['sm', 'md']),
  ariaLabel: PropTypes.string,
  onSeek: PropTypes.func,
  className: PropTypes.string,
};
